import React, { useEffect, useMemo, useRef, useState } from "react";
import { endpoints, API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  FolderOpen, UploadCloud, Trash2, FileText, FileImage, FileType2, ExternalLink, X,
} from "lucide-react";

const KIND_LABELS = {
  "check-in": "Check-in",
  "check-in-before": "Then",
  "check-in-after": "Now",
  "spa-before": "Wellness · before",
  "spa-after": "Wellness · after",
  "journal": "Journal",
  "library": "Library",
  "procedure-doc": "Procedure doc",
};

const FILTERS = [
  { slug: null, label: "All" },
  { slug: "check-in", label: "Check-ins" },
  { slug: "spa-before", label: "Wellness · before" },
  { slug: "spa-after", label: "Wellness · after" },
  { slug: "procedure-doc", label: "Docs" },
  { slug: "journal", label: "Journal" },
  { slug: "library", label: "Library" },
];

export default function MediaLibrary() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, total_bytes: 0, by_kind: {} });
  const [filter, setFilter] = useState(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    try {
      const [{ data }, { data: s }] = await Promise.all([endpoints.library(filter), endpoints.libraryStats()]);
      setItems(data);
      setStats(s);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "library");
      await endpoints.libraryUpload(fd);
      toast.success("Filed away.");
      load();
    } catch (er) {
      toast.error(er?.response?.data?.detail || "Upload failed.");
    }
    setUploading(false);
  };

  const remove = async (id) => {
    try {
      await endpoints.deleteLibraryItem(id);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success("Removed.");
    } catch {
      toast.error("Could not remove.");
    }
  };

  return (
    <section className="p-8 border hairline rounded-md bg-[hsl(var(--paper))] reveal reveal-4" data-testid="media-library">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))] inline-flex items-center gap-2">
            <FolderOpen size={12} className="text-[hsl(var(--clay))]" /> Files &amp; media
          </div>
          <h2 className="font-display text-4xl mt-2 tracking-tight italic">Your library</h2>
          <p className="mt-2 max-w-xl text-sm text-[hsl(var(--muted-foreground))]">
            Everything you have quietly saved — check-in photos, before &amp; afters from your wellness practice, and procedure documents.
            All held in one calm place.
          </p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Total</div>
          <div className="font-display text-3xl tabular-nums leading-none mt-1" data-testid="library-total">{stats.total}</div>
          <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">{formatBytes(stats.total_bytes)}</div>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="library-filters">
        {FILTERS.map((f) => (
          <button
            key={f.label}
            onClick={() => setFilter(f.slug)}
            data-testid={`library-filter-${f.slug || "all"}`}
            className={`px-3 py-1.5 rounded-full text-xs tab-link ${
              filter === f.slug ? "bg-[hsl(var(--fg))] text-[hsl(var(--paper))]" : "border hairline text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))]"
            }`}
          >
            {f.label}
            {f.slug && stats.by_kind[f.slug] > 0 && (
              <span className="ml-1.5 opacity-70 tabular-nums">{stats.by_kind[f.slug]}</span>
            )}
          </button>
        ))}
        <div className="ml-auto">
          <input ref={inputRef} type="file" className="hidden" onChange={onFile} accept="image/*,application/pdf,text/plain,text/markdown,text/csv" data-testid="library-file-input" />
          <Button
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid="library-upload-btn"
            className="rounded-full bg-[hsl(var(--terracotta))] text-[hsl(var(--fg))] hover:bg-[hsl(var(--terracotta))]/90"
          >
            <UploadCloud size={14} className="mr-2" /> {uploading ? "Filing…" : "Upload file"}
          </Button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="mt-6 p-8 border hairline rounded-md bg-[hsl(var(--sand))]/30 text-center text-sm text-[hsl(var(--muted-foreground))]" data-testid="library-empty">
          Nothing here yet. Upload a document or wait for a check-in to appear.
        </div>
      ) : (
        <ul className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-testid="library-grid">
          {items.map((it) => <LibraryTile key={it.id} item={it} onRemove={() => remove(it.id)} />)}
        </ul>
      )}
    </section>
  );
}

function LibraryTile({ item, onRemove }) {
  const url = `${API}/files/${item.storage_path}`;
  const isImage = !!item.is_image || (item.content_type || "").startsWith("image/");
  const ext = (item.original_filename || "").split(".").pop()?.toLowerCase() || "";
  const [confirm, setConfirm] = useState(false);
  return (
    <li className="group relative border hairline rounded-md overflow-hidden bg-[hsl(var(--sand))]/30" data-testid={`library-item-${item.id}`}>
      <a href={url} target="_blank" rel="noreferrer" className="block relative aspect-square" title={item.original_filename}>
        {isImage ? (
          <img src={url} alt={item.original_filename || "file"} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full grid place-items-center text-[hsl(var(--muted-foreground))] p-4">
            {ext === "pdf" ? <FileType2 size={32} /> : <FileText size={32} />}
            <div className="mt-2 text-[10px] uppercase tracking-[0.2em]">{ext || "file"}</div>
          </div>
        )}
        <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-[hsl(var(--fg))]/70 text-[hsl(var(--paper))] text-[9px] uppercase tracking-[0.18em]">
          {KIND_LABELS[item.kind] || item.kind}
        </span>
        <ExternalLink size={12} className="absolute top-2 right-2 text-[hsl(var(--paper))]/80 mix-blend-difference" />
      </a>
      <div className="p-2.5 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-xs truncate">{item.original_filename || "untitled"}</div>
          <div className="text-[10px] text-[hsl(var(--muted-foreground))]">{formatBytes(item.size)} · {niceDate(item.created_at)}</div>
        </div>
        {!confirm ? (
          <button
            onClick={() => setConfirm(true)}
            data-testid={`library-delete-${item.id}`}
            aria-label="Remove file"
            className="p-1 rounded-full text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))]"
          >
            <Trash2 size={12} />
          </button>
        ) : (
          <div className="flex items-center gap-1">
            <button
              onClick={() => { onRemove(); setConfirm(false); }}
              data-testid={`library-delete-confirm-${item.id}`}
              className="px-2 py-0.5 rounded-full bg-[hsl(var(--destructive))] text-[hsl(var(--paper))] text-[10px]"
            >
              Remove
            </button>
            <button
              onClick={() => setConfirm(false)}
              className="p-1 rounded-full text-[hsl(var(--muted-foreground))]"
              aria-label="Cancel"
            >
              <X size={11} />
            </button>
          </div>
        )}
      </div>
    </li>
  );
}

function formatBytes(n) {
  if (!n || n < 1024) return `${n || 0} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
function niceDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
